import React from "react";
import CsvToTable from "src/components/Common/CsvToTable";
import { JsonView, allExpanded, defaultStyles } from "react-json-view-lite";
import { getFileType, isCSV, isXlsm, isXlsx } from "src/utils/common";
import { FILE_TYPE, SIMULATION_TYPE } from "src/constant/common";
import { FORM_NAME as FORM_NAME_3DOF } from "../ModalUploadFile3DOF";
import { FORM_NAME } from "../ModalUploadFile6DOF";

export const useFileContentUtils = (t) => {
  const isCSVContent = (content) => {
    if (typeof content !== "string") {
      return false;
    }
    const csvPattern = /^(?:[^,\n]+,)*[^,\n]+(?:\n|$)/;
    return csvPattern.test(content);
  };

  const showJsonContent = (jsonObj) => {
    if (typeof jsonObj === "string" && jsonObj.trim() === "") {
      return <div />;
    }
    if (typeof jsonObj !== "object") {
      return <div>{t("file_error")}</div>;
    }
    return <JsonView data={jsonObj} shouldExpandNode={allExpanded} style={{ ...defaultStyles, container: "bg-white" }} />;
  };

  const getAeroContent = (fileName = "", filePath = "", fileContent = "") => {
    const fileType = getFileType(fileName);

    if (fileType === FILE_TYPE.XLSX) {
      return (
        <a href={filePath} className="fw-bold link-primary text-break">
          {fileName}
        </a>
      );
    }

    return isCSV(fileName) && (isCSVContent(fileContent) ? <CsvToTable csvData={fileContent} /> : <div>{t("file_error")}</div>);
  };

  const isHasExcelFiles = (fileData) => {
    if (!fileData) return false;
    const fields = [
      fileData.aero1_name,
      fileData.aero2_name,
      fileData.aero1_ascent_name,
      fileData.aero2_ascent_name,
      fileData.aero1_descent_name,
      fileData.thrust_profile_name,
    ];

    if (fileData.aero_files && Array.isArray(fileData.aero_files)) {
      const aeroFileNames = fileData.aero_files.map((file) => file.aero_name);
      fields.push(...aeroFileNames);
    }

    return fields.some((field) => field && (getFileType(field) === FILE_TYPE.XLSX || getFileType(field) === FILE_TYPE.XLSM));
  };

  const getExcelFiles = (fileData, simulationType, isNewVersion) => {
    if (!fileData) return [];

    if (simulationType === SIMULATION_TYPE["3DOF"]) {
      const fields = [];

      if (isNewVersion) {
        // For new version, use aero_files
        if (fileData.aero_files && Array.isArray(fileData.aero_files)) {
          const aeroExcelFiles = fileData.aero_files
            .filter((file) => file.aero_name && isXlsx(file.aero_name))
            .map((file) => ({ title: file.aero_name, name: file.aero_name, link: file.aero_link }));
          fields.push(...aeroExcelFiles);
        }
      } else {
        // For old version, use aero1 and aero2
        if (fileData?.aero1_name) {
          fields.push({ title: t(`new.${FORM_NAME_3DOF.AERO_COEFFICIENTS_1}`), name: fileData?.aero1_name, link: fileData?.aero1_link });
        }
        if (fileData?.aero2_name) {
          fields.push({ title: t(`new.${FORM_NAME_3DOF.AERO_COEFFICIENTS_2}`), name: fileData?.aero2_name, link: fileData?.aero2_link });
        }
      }

      return fields.filter((field) => field.name && (isXlsx(field.name) || isXlsm(field.name)));
    }

    const fields = [
      { title: t(`new.${FORM_NAME.AERO_COEFFICIENTS_ASCENT}`), name: fileData?.aero1_ascent_name, link: fileData?.aero1_ascent_link },
      { title: t(`new.${FORM_NAME.AERO_COEFFICIENTS_ASCENT_2}`), name: fileData?.aero2_ascent_name, link: fileData?.aero2_ascent_link },
      { title: t(`new.${FORM_NAME.AERO_COEFFICIENTS_DESCENT}`), name: fileData?.aero_descent_name, link: fileData?.aero_descent_link },
      { title: t(`new.${FORM_NAME.THRUST_PROFILE}`), name: fileData?.thrust_profile_name, link: fileData?.thrust_profile_link },
    ];

    return fields.filter((field) => field.name && (isXlsx(field.name) || isXlsm(field.name)));
  };

  const getExcelFilesContent = (fileData, simulationType, isNewVersion) => {
    const fileExcels = getExcelFiles(fileData, simulationType, isNewVersion);

    const aeroFilesExcel =
      fileData?.aero_files && Array.isArray(fileData.aero_files)
        ? fileData.aero_files.filter((file) => file.aero_name && (isXlsx(file.aero_name) || isXlsm(file.aero_name)))
        : [];

    const otherExcelFiles = fileExcels?.filter((file) => !aeroFilesExcel.some((aeroFile) => aeroFile.aero_name === file.name)) || [];

    return (
      <>
        {aeroFilesExcel.length > 0 && (
          <div className="mb-3">
            <span className="fw-bold">{`${t(`new.${FORM_NAME_3DOF.AERO_COEFFICIENTS}`)}: `}</span>
            <br />
            {aeroFilesExcel.map((aeroFile, index) => (
              <span key={index}>
                <a href={aeroFile.aero_link} className="fw-bold link-primary text-break">
                  {aeroFile.aero_name}
                </a>
                {index < aeroFilesExcel.length - 1 && ", "}
              </span>
            ))}
          </div>
        )}

        {otherExcelFiles.map((file, index) => (
          <div className="mb-3" key={`other-${index}`}>
            <span className="fw-bold">{file?.title}: </span>
            <br />
            {(isXlsx(file?.name) || isXlsm(file?.name)) && (
              <a href={file?.link} className="fw-bold link-primary text-break">
                {file?.name}
              </a>
            )}
          </div>
        ))}
      </>
    );
  };

  return {
    isCSV,
    isXlsx,
    isXlsm,
    isCSVContent,
    showJsonContent,
    getAeroContent,
    isHasExcelFiles,
    getExcelFiles,
    getExcelFilesContent: (fileData, simulationType, isNewVersion) => getExcelFilesContent(fileData, simulationType, isNewVersion),
  };
};
